/**
 * Client-side USD normalization for international filers.
 * Works when the backend is stale or unavailable — infers FX from income vs earnings calendar.
 * Skips conversion for TSX .TO listings and when data is already in USD scale.
 */

const NATIVE_LISTING_SUFFIXES = [".TO"];

const INCOME_MONETARY = new Set([
  "revenue", "costOfRevenue", "grossProfit", "operatingExpenses", "operatingIncome",
  "incomeBeforeTax", "incomeTaxExpense", "netIncome", "ebitda", "eps", "epsdiluted",
  "interestIncome", "interestExpense", "depreciationAndAmortization",
  "researchAndDevelopmentExpenses", "generalAndAdministrativeExpenses",
  "sellingAndMarketingExpenses", "sellingGeneralAndAdministrativeExpenses",
]);

const CASHFLOW_MONETARY = new Set([
  "netIncome", "operatingCashFlow", "freeCashFlow", "capitalExpenditure",
  "netCashProvidedByOperatingActivities", "netCashUsedForInvestingActivites",
  "netCashUsedProvidedByFinancingActivities", "dividendsPaid",
]);

const BALANCE_MONETARY = new Set([
  "totalAssets", "totalLiabilities", "totalStockholdersEquity", "totalDebt", "netDebt",
  "cashAndCashEquivalents", "totalCurrentAssets", "totalCurrentLiabilities", "longTermDebt",
]);

const ANALYST_MONETARY = new Set([
  "estimatedRevenueLow", "estimatedRevenueHigh", "estimatedRevenueAvg",
  "estimatedEpsAvg", "estimatedEpsHigh", "estimatedEpsLow",
  "estimatedEPS", "estimatedEps", "estimatedEPSAvg",
  "epsEstimate", "epsEstimated", "revenueEstimate", "estimatedRevenue", "revenueEstimated",
]);

const STATEMENT_MAP = {
  income: INCOME_MONETARY,
  annualIncome: INCOME_MONETARY,
  cashflow: CASHFLOW_MONETARY,
  annualCashflow: CASHFLOW_MONETARY,
  balanceSheet: BALANCE_MONETARY,
  annualBalanceSheet: BALANCE_MONETARY,
};

function median(nums) {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function usesNativeListingCurrency(symbol) {
  const sym = (symbol || "").toUpperCase();
  return NATIVE_LISTING_SUFFIXES.some((suffix) => sym.endsWith(suffix));
}

export function getReportedCurrency(fundamentals) {
  for (const key of ["income", "annualIncome"]) {
    const row = fundamentals?.[key]?.[0];
    const raw = row?.reportedCurrency || row?.currency;
    if (raw) return String(raw).toUpperCase();
  }
  return fundamentals?.currencyMeta?.reportedCurrency || "USD";
}

function inferFxFromIncomeAndCalendar(income, earningsHistory) {
  const calRev = {};
  for (const e of earningsHistory || []) {
    const fde = e.fiscalDateEnding?.slice?.(0, 10) || e.fiscalDateEnding;
    if (fde && e.revenue > 0) calRev[fde] = e.revenue;
  }
  const ratios = [];
  for (const row of income || []) {
    const d = row.date?.slice?.(0, 10) || row.date;
    const rev = row.revenue;
    if (d && rev > 0 && calRev[d]) ratios.push(calRev[d] / rev);
  }
  if (ratios.length < 2) return null;
  const rate = median(ratios);
  return rate > 0.005 && rate < 2 ? rate : null;
}

function inferFxFromAnalystAndCalendar(analystEstimates, earningsHistory) {
  const calRev = {};
  for (const e of earningsHistory || []) {
    const fde = e.fiscalDateEnding?.slice?.(0, 10) || e.fiscalDateEnding;
    if (fde && e.revenueEstimated > 0) calRev[fde] = e.revenueEstimated;
  }
  const ratios = [];
  for (const row of analystEstimates || []) {
    const d = (row.date || row.fiscalDateEnding)?.slice?.(0, 10);
    const rev = row.estimatedRevenueAvg ?? row.estimatedRevenue;
    if (d && rev > 0 && calRev[d]) ratios.push(calRev[d] / rev);
  }
  if (ratios.length < 2) return null;
  const rate = median(ratios);
  return rate > 0.005 && rate < 2 ? rate : null;
}

export function resolveFxToUsd(symbol, fundamentals) {
  if (!fundamentals) return 1;
  if (usesNativeListingCurrency(symbol)) return 1;

  const fromIncome = inferFxFromIncomeAndCalendar(
    fundamentals.income,
    fundamentals.earningsHistory
  );
  const fromAnalyst = inferFxFromAnalystAndCalendar(
    fundamentals.analystEstimates,
    fundamentals.earningsHistory
  );
  const fx = fromIncome ?? fromAnalyst ?? 1;
  if (Math.abs(fx - 1) < 0.02) return 1;
  return fx;
}

function scaleRows(rows, fields, fx) {
  if (!rows?.length || Math.abs(fx - 1) < 0.001) return rows;
  return rows.map((row) => {
    const out = { ...row };
    for (const key of fields) {
      if (typeof out[key] === "number" && Number.isFinite(out[key])) {
        out[key] *= fx;
      }
    }
    return out;
  });
}

function scaleForecastPoints(points, fx) {
  if (!points?.length || Math.abs(fx - 1) < 0.001) return points;
  return points.map((p) => ({
    ...p,
    date: p.date || p.fiscalDateEnding,
    estimate: typeof p.estimate === "number" ? p.estimate * fx : p.estimate,
  }));
}

function forecastsAlreadyUsd(forecasts, fundamentals) {
  const first = forecasts?.revenue?.next8?.[0];
  if (!first?.estimate || first.estimate <= 0) return false;
  const fde = first.date || first.fiscalDateEnding;
  if (!fde) return first.estimate < 40e9;

  const cal = (fundamentals?.earningsHistory || []).find(
    (e) => e.fiscalDateEnding === fde || e.fiscalDateEnding?.slice(0, 10) === fde
  );
  if (cal?.revenueEstimated > 0) {
    return Math.abs(first.estimate - cal.revenueEstimated) / cal.revenueEstimated < 0.2;
  }
  return first.estimate < 40e9;
}

/**
 * Normalize fundamentals bundle to USD (mutates and returns the same object).
 */
export function normalizeFundamentalsToUsd(symbol, fundamentals) {
  if (!fundamentals || usesNativeListingCurrency(symbol)) return fundamentals;
  if (fundamentals.currencyMeta?.convertedToUsd) return fundamentals;
  if (fundamentals.currencyMeta?.nativeListing) return fundamentals;

  const fx = resolveFxToUsd(symbol, fundamentals);
  fundamentals.currencyMeta = {
    reportedCurrency: getReportedCurrency(fundamentals),
    fxToUsd: fx,
    convertedToUsd: Math.abs(fx - 1) >= 0.001,
    convertedOn: fundamentals.currencyMeta?.convertedOn || "client",
  };

  if (Math.abs(fx - 1) < 0.001) return fundamentals;

  for (const [key, fields] of Object.entries(STATEMENT_MAP)) {
    if (Array.isArray(fundamentals[key])) {
      fundamentals[key] = scaleRows(fundamentals[key], fields, fx);
    }
  }
  if (Array.isArray(fundamentals.analystEstimates)) {
    fundamentals.analystEstimates = scaleRows(fundamentals.analystEstimates, ANALYST_MONETARY, fx);
  }
  if (Array.isArray(fundamentals.annualAnalystEstimates)) {
    fundamentals.annualAnalystEstimates = scaleRows(
      fundamentals.annualAnalystEstimates,
      ANALYST_MONETARY,
      fx
    );
  }
  fundamentals.currencyMeta.convertedOn = "client";
  return fundamentals;
}

/** Normalize analyst forecast boxes (next8 EPS/revenue) to USD. */
export function normalizeAnalystForecastsToUsd(symbol, forecasts, fundamentals) {
  if (!forecasts || usesNativeListingCurrency(symbol)) return forecasts;
  if (forecastsAlreadyUsd(forecasts, fundamentals)) return forecasts;

  const fx =
    fundamentals?.currencyMeta?.fxToUsd != null && fundamentals.currencyMeta.fxToUsd > 0
      ? fundamentals.currencyMeta.fxToUsd
      : resolveFxToUsd(symbol, fundamentals);

  if (Math.abs(fx - 1) < 0.001) return forecasts;

  return {
    eps: {
      ...forecasts.eps,
      next8: scaleForecastPoints(forecasts.eps?.next8, fx),
    },
    revenue: {
      ...forecasts.revenue,
      next8: scaleForecastPoints(forecasts.revenue?.next8, fx),
    },
  };
}
